/**
 * ColorTheme.ts — Color constants and utilities for terminal rendering.
 *
 * Defines the game's color palette using ANSI escape codes compatible with xterm.js.
 * Supports colorblind mode (adds text markers) and high-contrast mode.
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest).
 */

export type ColorName =
  | 'white' | 'yellow' | 'green' | 'red' | 'cyan' | 'magenta' | 'gray' | 'brightWhite' | 'black' | 'blue'
  | 'frostBlue' | 'frostSilver' | 'runeGold' | 'deepVoid' | 'icyWhite' | 'khaki' | 'darkAmber'
  | 'amber' | 'cream' | 'corruptionPurple' | 'frostbiteCyan' | 'charcoal'
  | 'sepia' | 'warmRed';

/** ANSI escape code prefix */
const ESC = '\x1b[';

/** ANSI color codes for foreground text */
const ANSI_COLORS: Record<ColorName, string> = {
  black:       `${ESC}30m`,
  red:         `${ESC}31m`,
  green:       `${ESC}32m`,
  yellow:      `${ESC}33m`,
  blue:        `${ESC}34m`,
  magenta:     `${ESC}35m`,
  cyan:        `${ESC}36m`,
  white:       `${ESC}37m`,
  gray:        `${ESC}90m`,
  brightWhite: `${ESC}97m`,
  // Gallowcrest frost palette (xterm-256)
  frostBlue:        `${ESC}38;5;75m`,    // #4A9EFF — accent borders, glowing runes
  frostSilver:      `${ESC}38;5;152m`,   // #C0C8D8 — text, rivets, metallic accents
  runeGold:         `${ESC}38;5;178m`,   // #D4A520 — rune highlights, warm accent
  deepVoid:         `${ESC}38;5;233m`,   // #0A0A0E — background, darkest tone
  icyWhite:         `${ESC}38;5;195m`,   // #D7FFFF — highlighted/important text
  khaki:            `${ESC}38;5;144m`,   // #AFAF87 — labels, secondary text
  darkAmber:        `${ESC}38;5;172m`,   // #D78700 — frame borders, separators
  amber:            `${ESC}38;5;214m`,   // #FFB000 — headers, borders, primary UI
  cream:            `${ESC}38;5;230m`,   // #FFFFD7 — highlighted text
  corruptionPurple: `${ESC}38;5;135m`,   // #8A4FFF — corruption indicators
  frostbiteCyan:    `${ESC}38;5;45m`,    // #00D4FF — frostbite indicators
  charcoal:         `${ESC}38;5;239m`,   // #4E4E4E — disabled/locked items
  sepia:            `${ESC}38;5;180m`,   // #D7AF87 — narrative body text (from RK)
  warmRed:          `${ESC}38;5;167m`,   // #D75F5F — danger/exit (warmer red, from RK)
};

/** ANSI background color codes */
const ANSI_BG_COLORS: Record<ColorName, string> = {
  black:       `${ESC}40m`,
  red:         `${ESC}41m`,
  green:       `${ESC}42m`,
  yellow:      `${ESC}43m`,
  blue:        `${ESC}44m`,
  magenta:     `${ESC}45m`,
  cyan:        `${ESC}46m`,
  white:       `${ESC}47m`,
  gray:        `${ESC}100m`,
  brightWhite: `${ESC}107m`,
  // Gallowcrest frost palette backgrounds (xterm-256)
  frostBlue:        `${ESC}48;5;75m`,
  frostSilver:      `${ESC}48;5;152m`,
  runeGold:         `${ESC}48;5;178m`,
  deepVoid:         `${ESC}48;5;233m`,
  icyWhite:         `${ESC}48;5;195m`,
  khaki:            `${ESC}48;5;144m`,
  darkAmber:        `${ESC}48;5;172m`,
  amber:            `${ESC}48;5;214m`,
  cream:            `${ESC}48;5;230m`,
  corruptionPurple: `${ESC}48;5;135m`,
  frostbiteCyan:    `${ESC}48;5;45m`,
  charcoal:         `${ESC}48;5;239m`,
  sepia:            `${ESC}48;5;180m`,
  warmRed:          `${ESC}48;5;167m`,
};

/** Reset all formatting */
export const RESET = `${ESC}0m`;

/** Bold text */
export const BOLD = `${ESC}1m`;

/** Reverse video (swap fg/bg) — used for menu highlight */
export const REVERSE = `${ESC}7m`;

/**
 * Game color conventions:
 * - yellow:  status bars, section headers, important labels
 * - green:   positive outcomes (healing, success, XP gains)
 * - red:     negative outcomes (damage, danger, failures), exit options
 * - white:   narrative text, descriptions, standard content
 * - cyan:    hints, help text, system messages, skill check info
 * - magenta: magic, rare items, special events, class abilities
 * - blue:    NPC names, location names
 * - gray:    disabled options only (locked items, empty slots)
 */

export class ColorTheme {
  private colorblindMode = false;
  private highContrastMode = false;

  /** Get ANSI foreground color code (with high-contrast remapping) */
  fg(color: ColorName): string {
    const mapped = this.highContrastMode ? this.remapHighContrast(color) : color;
    return ANSI_COLORS[mapped] ?? ANSI_COLORS.white;
  }

  /** Get ANSI background color code */
  bg(color: ColorName): string {
    return ANSI_BG_COLORS[color] ?? ANSI_BG_COLORS.black;
  }

  /** Wrap text in a foreground color with auto-reset */
  colorize(text: string, color: ColorName): string {
    const prefix = this.getSymbolPrefix(color);
    return `${this.fg(color)}${prefix}${text}${RESET}`;
  }

  /** Wrap text in bold + foreground color with auto-reset */
  boldColor(text: string, color: ColorName): string {
    const prefix = this.getSymbolPrefix(color);
    return `${BOLD}${this.fg(color)}${prefix}${text}${RESET}`;
  }

  /** Wrap text in reverse video (for menu highlighting) */
  highlight(text: string): string {
    return `${REVERSE}${text}${RESET}`;
  }

  /** Enable/disable colorblind mode (adds text markers alongside colors) */
  setColorblindMode(enabled: boolean): void {
    this.colorblindMode = enabled;
  }

  isColorblindMode(): boolean {
    return this.colorblindMode;
  }

  /** Enable/disable high-contrast mode */
  setHighContrastMode(enabled: boolean): void {
    this.highContrastMode = enabled;
  }

  isHighContrastMode(): boolean {
    return this.highContrastMode;
  }

  /** Get symbol prefix for colorblind mode. Public so scenes can add symbols at semantic feedback points. */
  getSymbolPrefix(color: ColorName): string {
    if (!this.colorblindMode) return '';
    switch (color) {
      case 'green':   return '[+] ';
      case 'red':     return '[-] ';
      case 'yellow':  return '[!] ';
      case 'magenta': return '[*] ';
      case 'cyan':    return '[?] ';
      default:        return '';
    }
  }

  /** Remap colors for better readability on dark backgrounds in high-contrast mode */
  private remapHighContrast(color: ColorName): ColorName {
    switch (color) {
      // Make dim colors brighter
      case 'gray':        return 'white';
      case 'charcoal':    return 'white';
      case 'blue':        return 'cyan';
      case 'khaki':       return 'cream';
      case 'sepia':       return 'cream';
      case 'frostSilver': return 'icyWhite';
      case 'darkAmber':   return 'amber';

      // Boost already-bright colors
      case 'white':       return 'brightWhite';
      case 'cream':       return 'brightWhite';
      case 'cyan':        return 'frostbiteCyan';
      case 'green':       return 'green'; // Already bright
      case 'red':         return 'red';    // Already bright

      default:            return color;
    }
  }
}

/** Singleton theme instance used throughout the game */
export const theme = new ColorTheme();
